/**
 * Collab provider factory — connects to a Premier WS when URL is set.
 * Without a WS URL, editors keep REST sync + newest-chunk-wins.
 */

import * as Y from "yjs";
import * as awarenessProtocol from "y-protocols/awareness";

export type CollabProviderStatus = "idle" | "connecting" | "connected" | "offline" | "error";

export type CollabProviderHandle = {
  doc: Y.Doc;
  /** Yjs awareness for CollaborationCaret / presence. */
  awareness: awarenessProtocol.Awareness;
  status: () => CollabProviderStatus;
  destroy: () => void;
};

export type CreateCollabProviderOptions = {
  chapterId: string;
  bookId: string;
  /** Short-lived room token from POST /api/collab/token */
  token: string;
  /** e.g. wss://collab.heyjustwrite.com */
  websocketUrl: string;
  doc?: Y.Doc;
  user?: { name: string; color: string };
  onStatus?: (status: CollabProviderStatus) => void;
};

function toBase64(bytes: Uint8Array): string {
  if (typeof Buffer !== "undefined") {
    return Buffer.from(bytes).toString("base64");
  }
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
  return btoa(binary);
}

function fromBase64(b64: string): Uint8Array {
  if (typeof Buffer !== "undefined") {
    return new Uint8Array(Buffer.from(b64, "base64"));
  }
  const binary = atob(b64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

/**
 * Native WebSocket Yjs + awareness sync.
 * Protocol: auth JSON, binary Yjs updates, JSON `{ type:"awareness", update }` (base64).
 */
export function createCollabProvider(options: CreateCollabProviderOptions): CollabProviderHandle {
  const doc = options.doc ?? new Y.Doc();
  doc.guid = options.chapterId;
  const awareness = new awarenessProtocol.Awareness(doc);
  if (options.user) {
    awareness.setLocalStateField("user", options.user);
  }

  let status: CollabProviderStatus = "connecting";
  options.onStatus?.(status);

  const url = new URL(options.websocketUrl);
  url.searchParams.set("bookId", options.bookId);
  url.searchParams.set("chapterId", options.chapterId);
  url.searchParams.set("token", options.token);

  let socket: WebSocket | null = null;
  let closed = false;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  const setStatus = (next: CollabProviderStatus) => {
    status = next;
    options.onStatus?.(next);
  };

  const sendUpdate = (update: Uint8Array, origin: unknown) => {
    if (origin === "remote" || !socket || socket.readyState !== WebSocket.OPEN) return;
    socket.send(update);
  };

  const onDocUpdate = (update: Uint8Array, origin: unknown) => {
    sendUpdate(update, origin);
  };

  const onAwarenessChange = (
    { added, updated, removed }: { added: number[]; updated: number[]; removed: number[] },
    origin: unknown
  ) => {
    if (origin === "remote" || !socket || socket.readyState !== WebSocket.OPEN) return;
    const changed = added.concat(updated, removed);
    if (changed.length === 0) return;
    const encoded = awarenessProtocol.encodeAwarenessUpdate(awareness, changed);
    socket.send(JSON.stringify({ type: "awareness", update: toBase64(encoded) }));
  };

  doc.on("update", onDocUpdate);
  awareness.on("update", onAwarenessChange);

  const connect = () => {
    if (closed) return;
    setStatus("connecting");
    try {
      socket = new WebSocket(url.toString());
    } catch {
      setStatus("error");
      return;
    }
    socket.binaryType = "arraybuffer";

    socket.onopen = () => {
      setStatus("connected");
      socket?.send(
        JSON.stringify({
          type: "auth",
          bookId: options.bookId,
          chapterId: options.chapterId,
          token: options.token,
        })
      );
      socket?.send(Y.encodeStateAsUpdate(doc));
      const encoded = awarenessProtocol.encodeAwarenessUpdate(awareness, [awareness.clientID]);
      socket?.send(JSON.stringify({ type: "awareness", update: toBase64(encoded) }));
    };

    socket.onmessage = (event) => {
      if (typeof event.data === "string") {
        try {
          const msg = JSON.parse(event.data) as {
            type?: string;
            error?: string;
            update?: string;
          };
          if (msg.type === "error") {
            setStatus("error");
            return;
          }
          if (msg.type === "awareness" && typeof msg.update === "string") {
            awarenessProtocol.applyAwarenessUpdate(awareness, fromBase64(msg.update), "remote");
          }
        } catch {
          /* ignore */
        }
        return;
      }
      const buf =
        event.data instanceof ArrayBuffer
          ? new Uint8Array(event.data)
          : event.data instanceof Uint8Array
            ? event.data
            : null;
      if (!buf) return;
      Y.applyUpdate(doc, buf, "remote");
    };

    socket.onclose = () => {
      setStatus("offline");
      socket = null;
      if (!closed) {
        reconnectTimer = setTimeout(connect, 2000);
      }
    };

    socket.onerror = () => {
      setStatus("error");
    };
  };

  connect();

  return {
    doc,
    awareness,
    status: () => status,
    destroy() {
      closed = true;
      doc.off("update", onDocUpdate);
      awareness.off("update", onAwarenessChange);
      awarenessProtocol.removeAwarenessStates(awareness, [awareness.clientID], "local");
      if (reconnectTimer) clearTimeout(reconnectTimer);
      socket?.close();
      socket = null;
      setStatus("idle");
    },
  };
}

/** True when live collab WS is configured for this build. */
export function isLiveCollabConfigured(): boolean {
  return Boolean(resolveCollabWebsocketUrl());
}

function readEnv(name: string): string | undefined {
  if (typeof process !== "undefined" && process.env?.[name]) {
    return process.env[name];
  }
  return undefined;
}

export function resolveCollabWebsocketUrl(): string | null {
  // Prefer process.env (Next.js / Node). For Vite SPA builds, access import.meta.env
  // with static property names so Vite can inline VITE_* at build time.
  let viteUrl: string | undefined;
  let nextPublicUrl: string | undefined;
  try {
    viteUrl = import.meta.env.VITE_COLLAB_WS_URL as string | undefined;
    nextPublicUrl = import.meta.env.NEXT_PUBLIC_COLLAB_WS_URL as string | undefined;
  } catch {
    /* non-Vite bundle */
  }
  const url =
    readEnv("NEXT_PUBLIC_COLLAB_WS_URL")?.trim() ||
    readEnv("VITE_COLLAB_WS_URL")?.trim() ||
    nextPublicUrl?.trim() ||
    viteUrl?.trim() ||
    "";
  if (!url || url === "0") return null;
  return url;
}
